import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Spin, Empty, Input, message } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { getMoodleProgramDetails, getManualCourseProgress } from '../services/moodleService';
import { fetchAssignmentInstructions, fetchAssignmentCriteria, saveAssignmentCriteria } from '../services/bulkEvaluationService';
import { Training } from '../types/training';
import BulkEvaluationBoard from '../components/BulkEvaluationBoard';

const { Title, Text, Paragraph } = Typography;

// A dedicated page rather than a Drawer: a Drawer closes on an outside click,
// which was silently discarding a trainer's in-progress bulk review.
const BulkEvaluationPage: React.FC = () => {
    const { trainingId, assignmentId } = useParams<{ trainingId: string; assignmentId: string }>();
    const navigate = useNavigate();
    const [training, setTraining] = useState<Training | null>(null);
    const [courseProgress, setCourseProgress] = useState<any>(null);
    const [instructions, setInstructions] = useState<string | null>(null);
    const [criteria, setCriteria] = useState<{ name: string }[]>([{ name: '' }]);
    const [savedCriteria, setSavedCriteria] = useState<{ name: string }[]>([]);
    const [savingCriteria, setSavingCriteria] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadCourseProgress = useCallback(async (courseId: number) => {
        const progress = await getManualCourseProgress(courseId);
        setCourseProgress(progress);
        return progress;
    }, []);

    useEffect(() => {
        (async () => {
            if (!trainingId) return;
            const currentTraining = await getMoodleProgramDetails(trainingId);
            setTraining(currentTraining);
            if (currentTraining?.courseId) {
                const progress = await loadCourseProgress(currentTraining.courseId);
                // The assignment's question is the same for every participant on it -
                // fetched once, from any one enrolled participant, for display here
                // rather than per row (see specs/2026-09-17-bulk-assignment-ai-evaluation.md).
                const firstParticipant = (progress?.participants || [])[0];
                if (firstParticipant && assignmentId) {
                    const participantRef = { userid: firstParticipant.userid, email: firstParticipant.email };
                    const [text, existingCriteria] = await Promise.all([
                        fetchAssignmentInstructions(currentTraining.courseId, participantRef, Number(assignmentId)),
                        fetchAssignmentCriteria(currentTraining.courseId, participantRef, Number(assignmentId)),
                    ]);
                    setInstructions(text);
                    setSavedCriteria(existingCriteria);
                    setCriteria(existingCriteria.length > 0 ? existingCriteria : [{ name: '' }]);
                }
            }
            setLoading(false);
        })();
    }, [trainingId, assignmentId, loadCourseProgress]);

    const handleBack = () => navigate(`/trainer-led-training-details/${trainingId}?mode=analyze`);

    // Criteria are assignment-level - unlike a row's own evaluation, there's
    // no per-participant "Save" click to piggyback persistence on here, so
    // this gets its own explicit action, same "don't write unless changed"
    // rule TrainerLedView.tsx's handleSaveEval already follows.
    const handleSaveCriteria = async () => {
        if (!assignmentId) return;
        setSavingCriteria(true);
        try {
            const wrote = await saveAssignmentCriteria(Number(assignmentId), criteria, savedCriteria);
            if (wrote) {
                const trimmed = criteria.filter(c => c.name.trim()).map(c => ({ name: c.name.trim() }));
                setSavedCriteria(trimmed);
                message.success('Criteria saved');
            } else {
                message.info('No changes to save');
            }
        } catch {
            message.error('Failed to save criteria');
        } finally {
            setSavingCriteria(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
    }

    const assignment = (courseProgress?.assignments || []).find((a: any) => String(a.id) === String(assignmentId));
    const assignmentName = assignment?.name || 'Assignment';
    const maxCredits = (courseProgress?.participants || [])
        .flatMap((p: any) => p.modules || [])
        .find((m: any) => String(m.assignmentid) === String(assignmentId))?.maxcredits ?? 100;

    if (!training || !training.courseId) {
        return (
            <div style={{ padding: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>Back</Button>
                <Empty description="Training not found" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
                Back
            </Button>
            <Title level={4} style={{ marginBottom: 0 }}>{training.trainingName}</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Evaluate All - {assignmentName}
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start', marginBottom: 20 }}>
                {instructions && (
                    <div style={{ flex: '1 1 420px', minWidth: 320, background: '#f5f5f5', padding: '16px', borderRadius: 8 }}>
                        <Text strong type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                            ASSIGNMENT QUESTION
                        </Text>
                        <Paragraph style={{ whiteSpace: 'pre-wrap', color: '#595959', margin: 0 }}>
                            {instructions}
                        </Paragraph>
                    </div>
                )}
                <div style={{ flex: '0 1 380px', minWidth: 300 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <Text strong style={{ fontSize: 13 }}>Evaluation Criteria</Text>
                        <Button
                            size="small"
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => setCriteria(prev => [...prev, { name: '' }])}
                            style={{ borderRadius: 6 }}
                        >
                            Add
                        </Button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                        {criteria.map((c, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <Input
                                    value={c.name}
                                    onChange={e => setCriteria(prev => prev.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                                    placeholder={i === 0 ? 'e.g. Clarity of explanation' : i === 1 ? 'e.g. Practical approach' : 'e.g. Depth of analysis'}
                                    style={{ flex: 1, borderRadius: 6, fontSize: 13 }}
                                />
                                <Button
                                    size="small"
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={criteria.length === 1}
                                    onClick={() => setCriteria(prev => prev.filter((_, idx) => idx !== i))}
                                />
                            </div>
                        ))}
                    </div>
                    <Button size="small" type="primary" loading={savingCriteria} onClick={handleSaveCriteria} style={{ borderRadius: 6 }}>
                        Save Criteria
                    </Button>
                </div>
            </div>
            <BulkEvaluationBoard
                courseId={training.courseId}
                assignmentId={Number(assignmentId)}
                maxCredits={maxCredits}
                courseProgress={courseProgress}
                onSaved={() => training.courseId && loadCourseProgress(training.courseId)}
            />
        </div>
    );
};

export default BulkEvaluationPage;
